import { ArrowUp, ChevronDown, Mail, MapPin, Menu, MessageCircle, Phone, Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { SocialIcon } from '../components/SocialIcon'
import { integrations } from '../data/integrations'
import { SubscribeFrame } from '../components/SubscribeFrame'
import type { LanguageCode } from '../data/languages'
import {
  legacyLogo,
  type NavigationGroup,
  type NavigationItem,
} from '../data/navigation'
import { toMobileNavigationGroups, toNavigationItems } from '../data/navigationSettings'
import { translate } from '../data/translations'
import { useContactSettings } from '../hooks/useContactSettings'
import { useNavigationSettings } from '../hooks/useNavigationSettings'

type SiteLayoutProps = {
  children: ReactNode
  language: LanguageCode
}

export function SiteLayout({ children, language }: SiteLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { settings: contactSettings } = useContactSettings()
  const { items: editableNavigationItems } = useNavigationSettings()
  const mainNavigation = toNavigationItems(editableNavigationItems)
  const mobileNavigationGroups = toMobileNavigationGroups(editableNavigationItems)
  const footerNavigationGroups = mobileNavigationGroups

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.body.classList.toggle('mobile-menu-open', isMobileMenuOpen)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.classList.remove('mobile-menu-open')
    }
  }, [isMobileMenuOpen])

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href={`/${language}/`}>
          <img alt={legacyLogo.alt} decoding="async" height="117" src={legacyLogo.header} width="220" />
        </a>
        <nav aria-label="Ana navigasyon" className="desktop-navigation">
          {mainNavigation.map((item) => (
            <div className="nav-item" key={item.label.tr}>
              <a href={item.href[language]}>{item.label[language]}</a>
              {item.children ? (
                <div className="nav-dropdown">
                  {item.children.map((child) => (
                    <a href={child.href[language]} key={child.label.tr}>
                      {child.label[language]}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </nav>
        <div className="header-actions">
          <LanguageSwitcher activeLanguage={language} />
          <a className="call-link" href={contactSettings.phoneHref} aria-label="Telefon">
            <Phone size={18} />
          </a>
          <button
            aria-controls="mobile-navigation"
            aria-expanded={isMobileMenuOpen}
            aria-label="Menu"
            className="mobile-menu-button"
            type="button"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu size={22} />
          </button>
        </div>
      </header>
      <MobileNavigation
        contactSettings={contactSettings}
        isOpen={isMobileMenuOpen}
        language={language}
        groups={mobileNavigationGroups}
        onClose={() => setIsMobileMenuOpen(false)}
      />
      {children}
      <SiteFooter contactSettings={contactSettings} groups={footerNavigationGroups} language={language} />
      <MobileQuickActions contactSettings={contactSettings} />
    </div>
  )
}

function MobileQuickActions({ contactSettings }: { contactSettings: ReturnType<typeof useContactSettings>['settings'] }) {
  const [isOpen, setIsOpen] = useState(false)

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setIsOpen(false)
  }

  return (
    <div className={`mobile-quick-actions ${isOpen ? 'is-open' : ''}`}>
      <div className="mobile-quick-actions-menu" id="mobile-quick-actions-menu">
        <a href={contactSettings.phoneHref}>
          <Phone size={18} />
          <span>Telefon et</span>
        </a>
        <a href={contactSettings.whatsappHref} rel="noreferrer" target="_blank">
          <MessageCircle size={18} />
          <span>WhatsApp</span>
        </a>
        <a href={contactSettings.directionsUrl} rel="noreferrer" target="_blank">
          <MapPin size={18} />
          <span>Yol tarifi al</span>
        </a>
      </div>
      <button aria-label="Uste don" className="mobile-scroll-top" onClick={scrollToTop} type="button">
        <ArrowUp size={20} />
      </button>
      <button
        aria-controls="mobile-quick-actions-menu"
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Hizli erisim menusu kapat' : 'Hizli erisim menusu ac'}
        className="mobile-quick-actions-toggle"
        onClick={() => setIsOpen((value) => !value)}
        type="button"
      >
        {isOpen ? <X size={24} /> : <Plus size={24} />}
      </button>
    </div>
  )
}

function SiteFooter({
  contactSettings,
  groups,
  language,
}: {
  contactSettings: ReturnType<typeof useContactSettings>['settings']
  groups: NavigationGroup[]
  language: LanguageCode
}) {
  const socialLinks = contactSettings.socials
    .filter((item) => item.enabled)
    .sort((first, second) => first.order - second.order)

  return (
    <footer className="site-footer">
      <div className="site-footer-brand">
        <img alt={legacyLogo.alt} decoding="async" height="117" loading="lazy" src={legacyLogo.header} width="220" />
        <p>{translate('footer.address', language)}</p>
        <a className="site-footer-contact-link" href={contactSettings.phoneHref}>
          <Phone size={16} />
          {contactSettings.phoneDisplay}
        </a>
        <a className="site-footer-contact-link" href={contactSettings.whatsappHref} rel="noreferrer" target="_blank">
          <MessageCircle size={16} />
          Whatsapp: {contactSettings.whatsappDisplay}
        </a>
        <a className="site-footer-contact-link" href={`mailto:${contactSettings.email}`}>
          <Mail size={16} />
          {contactSettings.email}
        </a>
        <div className="site-footer-socials">
          {socialLinks.map((item) => (
            <a href={item.href} key={item.label} rel="noreferrer" target="_blank">
              <span aria-hidden="true">
                <SocialIcon name={item.icon} />
              </span>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <nav aria-label="Footer navigasyon" className="site-footer-navigation">
        {groups.map((group) => (
          <section className="site-footer-menu" key={group.label.tr}>
            <h2>{group.label[language]}</h2>
            {group.items.map((item) => (
              <a href={item.href[language]} key={item.label.tr}>
                {item.label[language]}
              </a>
            ))}
          </section>
        ))}
      </nav>

      {integrations.subscribe.enabled ? (
        <section className="site-footer-subscribe" aria-label={translate('sections.subscribe', language)}>
          <h2>{translate('sections.subscribe', language)}</h2>
          <SubscribeFrame />
        </section>
      ) : null}

      <div className="site-footer-bottom">
        <span>Modi Cafe</span>
        <span>© 2026</span>
      </div>
    </footer>
  )
}

function MobileNavigation({
  contactSettings,
  groups,
  isOpen,
  language,
  onClose,
}: {
  contactSettings: ReturnType<typeof useContactSettings>['settings']
  groups: NavigationGroup[]
  isOpen: boolean
  language: LanguageCode
  onClose: () => void
}) {
  return (
    <div
      aria-hidden={!isOpen}
      className={`mobile-navigation-layer ${isOpen ? 'is-open' : ''}`}
      id="mobile-navigation"
    >
      <button
        aria-label="Menuyu kapat"
        className="mobile-navigation-backdrop"
        type="button"
        onClick={onClose}
      />
      <aside aria-label="Mobil navigasyon" className="mobile-navigation-panel">
        <div className="mobile-navigation-head">
          <img alt={legacyLogo.alt} decoding="async" height="117" src={legacyLogo.header} width="220" />
          <button aria-label="Menuyu kapat" type="button" onClick={onClose}>
            <X size={22} />
          </button>
        </div>
        <LanguageSwitcher activeLanguage={language} />
        <a className="mobile-drawer-call" href={contactSettings.phoneHref} onClick={onClose}>
          <Phone size={18} />
          Hemen Ara
        </a>
        <nav>
          {groups.map((group) => (
            <MobileNavigationGroup
              group={group}
              key={group.label.tr}
              language={language}
              onClose={onClose}
            />
          ))}
        </nav>
      </aside>
    </div>
  )
}

function MobileNavigationGroup({
  group,
  language,
  onClose,
}: {
  group: NavigationGroup
  language: LanguageCode
  onClose: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const contentId = `mobile-group-${group.label.tr.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section className="mobile-nav-group">
      <button
        aria-controls={contentId}
        aria-expanded={isExpanded}
        className="mobile-nav-group-button"
        type="button"
        onClick={() => setIsExpanded((value) => !value)}
      >
        <span>{group.label[language]}</span>
        <ChevronDown size={18} />
      </button>
      <div className="mobile-nav-group-content" hidden={!isExpanded} id={contentId}>
        {group.items.map((item) => (
          <MobileNavigationItem item={item} key={item.label.tr} language={language} onClose={onClose} />
        ))}
      </div>
    </section>
  )
}

function MobileNavigationItem({
  item,
  language,
  onClose,
}: {
  item: NavigationItem
  language: LanguageCode
  onClose: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const contentId = `mobile-nav-${item.label.tr.replace(/\s+/g, '-').toLowerCase()}`

  if (!item.children) {
    return (
      <a className="mobile-nav-link" href={item.href[language]} onClick={onClose}>
        {item.label[language]}
      </a>
    )
  }

  return (
    <div className="mobile-nav-disclosure">
      <div className="mobile-nav-disclosure-row">
        <a className="mobile-nav-link" href={item.href[language]} onClick={onClose}>
          {item.label[language]}
        </a>
        <button
          aria-controls={contentId}
          aria-expanded={isExpanded}
          aria-label={`${item.label[language]} alt menulerini ${isExpanded ? 'kapat' : 'ac'}`}
          className="mobile-nav-toggle"
          onClick={() => setIsExpanded((value) => !value)}
          type="button"
        >
          <ChevronDown size={18} />
        </button>
      </div>
      <div className="mobile-nav-children" hidden={!isExpanded} id={contentId}>
        {item.children.map((child) => (
          <a href={child.href[language]} key={child.label.tr} onClick={onClose}>
            {child.label[language]}
          </a>
        ))}
      </div>
    </div>
  )
}
